import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';

import { BackendResponse, ContactDomainCheck } from '@qtek/shared/models';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly http = inject(HttpClient);

  userModels$ = new BehaviorSubject<BackendResponse | null>(null);

  /**
   * Get data such as company, person, relations of currently logged in user.
   *
   * TODO: Add generic type to return value.
   *
   * @returns {Observable<BackendResponse>}
   *
   */
  getCurrentUser(payload: any = {}): Observable<BackendResponse> {
    let params = new HttpParams();
    let headers = new HttpHeaders();

    if (payload.params?.prsId) {
      params = params.set('prsId', payload.params.prsId);
    }

    /**
     * Adding this header allows to redirect in auth guard instead of {@link RevalidateInterceptor}
     */
    if (payload.skipRevalidateErrorHandling) {
      headers = headers.append('X-Skip-Revalidate-Error-Handling', '');
    }
    return this.http.get<BackendResponse>('/api/v2/user', {
      withCredentials: true,
      params,
      headers,
    });
  }

  /**
   * Check if user of given email address exists in database.
   *
   * @param {string} email Email address to be checked.
   * @param {string} tp Additional type
   * @returns {Observable<{exist: boolean}>} Observable representing status of email.
   *
   * @memberof UserService
   */
  checkUniqueEmail(
    email: string,
    tp?: string,
    webId?: string
  ): Observable<{ exist: boolean }> {
    let params = new HttpParams().set('email', email);

    if (tp) {
      params = params.append('tp', tp);
    }

    if (webId) {
      params = params.append('webId', webId);
    }

    return this.http
      .get<{ res: { exist: boolean } }>('/api/v2/user/user-exists', {
        params,
      })
      .pipe(map(({ res }) => res));
  }

  checkAccountByEmail(email: string): Observable<ContactDomainCheck> {
    const params = new HttpParams().set('email', email);

    return this.http
      .get<BackendResponse<ContactDomainCheck>>(
        '/api/v2/company/contact-domain-check',
        {
          withCredentials: true,
          params,
        }
      )
      .pipe(map(({ res }) => res));
  }

  checkUniqueEmailForLogged(email: string): Observable<{ exist: boolean }> {
    const params = new HttpParams().set('email', email);

    return this.http
      .get<{ res: { exist: boolean } }>('/api/v2/user/user-exists', {
        withCredentials: true,
        params,
      })
      .pipe(map(({ res }) => res));
  }

  getPublicDataOfOtherUser(email: string) {
    const params = new HttpParams().set('email', email);

    return this.http.get<BackendResponse>('/api/v2/user', {
      withCredentials: true,
      params,
    });
  }

  getUsersModel(exItm?: string): Observable<BackendResponse> {
    return this.http
      .get<BackendResponse>('/api/v2/model/User', {
        withCredentials: true,
        params: exItm
          ? { view: 'candidatesForDeal', excludeAlreadyInvitedToItemId: exItm }
          : undefined,
      })
      .pipe(tap(models => this.userModels$.next(models)));
  }
}
